import React, { useEffect } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import {
  AssignmentInd,
  AutoStories,
  Description,
  EventNote,
  Hail,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import { useSearchParams } from "react-router-dom";
import useStore from "../../helpers/useStore";
import { TopLevelPaper } from "../../themes/StyledComponents";
import { RegistryContext } from "../../stores/Registry/registry.provider";
import { REGISTRY_SUBTABS } from "../../stores/Registry/registryStore";
import { useLocationId } from "../../stores/User/User.hooks";

import RegistryPatientsTab from "./tabs/RegistryPatientsTab";
import RegistryRxTab from "./tabs/RegistryRxTab";
import RegistryDoctorsTab from "./tabs/RegistryDoctorsTab";
import RegistryDoctorReportsTab from "./tabs/RegistryDoctorReportsTab";
import RegistryTreatmentNotesTab from "./tabs/RegistryTreatmentNotesTab";

/**
 * The "Register" / Registry page – a top-level browser for the core entities
 * of the system (Patients, Rx, Doctors/Clinics, Doctor Reports, Treatment Notes).
 *
 * Modelled after the Administration page: a horizontal Tabs row at the top
 * chooses an entity; each tab renders an entity-specific table (with its own
 * search/filter toolbar) below.
 *
 * The active tab and per-tab filter selections live in {@link RegistryStore}
 * (MobX). Data is fetched and cached via tanstack-query inside each tab.
 *
 * A `locationId` filter is implicitly applied based on `useLocationId()` and
 * propagated into each tab's filter state.
 */
const Registry = observer(() => {
  const RegistryStore = useStore(RegistryContext);
  const { activeTab, setActiveTab } = RegistryStore;

  const [searchParams, setSearchParams] = useSearchParams();
  const locationId = useLocationId();

  // ---- URL <-> store hydration for the active tab --------------------------
  useEffect(() => {
    const tabSelection = searchParams.get("tabSelection");
    if (
      tabSelection &&
      Object.values(REGISTRY_SUBTABS).includes(tabSelection as REGISTRY_SUBTABS) &&
      tabSelection !== activeTab
    ) {
      setActiveTab(tabSelection as REGISTRY_SUBTABS);
    }
    // intentionally only on mount – tab clicks below keep the URL in sync
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Propagate the current location into all tab filters -----------------
  useEffect(() => {
    if (!locationId) return;
    RegistryStore.setPatientFilters({ location: locationId });
    RegistryStore.setRxFilters({ location: locationId });
    RegistryStore.setDoctorReportFilters({ location: locationId });
    RegistryStore.setTreatmentNoteFilters({ location: locationId });
  }, [locationId, RegistryStore]);

  const handleTabClick = (tab: REGISTRY_SUBTABS) => {
    setActiveTab(tab);
    setSearchParams({ tabSelection: tab });
  };

  return (
    // Height-bounded flex column so the table inside each tab can be the real
    // scroll container (otherwise InfiniteScroll's scrollableTarget never
    // actually scrolls and never requests the next page).
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 80px)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexShrink: 0,
          height: "70px",
        }}
      >
        <Tabs
          value={activeTab}
          aria-label="Registry entity tabs"
          orientation="horizontal"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            iconPosition="start"
            icon={<AssignmentInd />}
            label="Patienten"
            value={REGISTRY_SUBTABS.PATIENTS}
            onClick={() => handleTabClick(REGISTRY_SUBTABS.PATIENTS)}
          />
          <Tab
            iconPosition="start"
            icon={<AutoStories />}
            label="Rezepte"
            value={REGISTRY_SUBTABS.RX}
            onClick={() => handleTabClick(REGISTRY_SUBTABS.RX)}
          />
          <Tab
            iconPosition="start"
            icon={<Hail />}
            label="Ärzte / Praxen"
            value={REGISTRY_SUBTABS.DOCTORS}
            onClick={() => handleTabClick(REGISTRY_SUBTABS.DOCTORS)}
          />
          <Tab
            iconPosition="start"
            icon={<Description />}
            label="Therapieberichte"
            value={REGISTRY_SUBTABS.DOCTOR_REPORTS}
            onClick={() => handleTabClick(REGISTRY_SUBTABS.DOCTOR_REPORTS)}
          />
          <Tab
            iconPosition="start"
            icon={<EventNote />}
            label="Tagesdoku"
            value={REGISTRY_SUBTABS.TREATMENT_NOTES}
            onClick={() => handleTabClick(REGISTRY_SUBTABS.TREATMENT_NOTES)}
          />
        </Tabs>
      </Box>
      <TopLevelPaper
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {activeTab === REGISTRY_SUBTABS.PATIENTS && <RegistryPatientsTab />}
          {activeTab === REGISTRY_SUBTABS.RX && <RegistryRxTab />}
          {activeTab === REGISTRY_SUBTABS.DOCTORS && <RegistryDoctorsTab />}
          {activeTab === REGISTRY_SUBTABS.DOCTOR_REPORTS && <RegistryDoctorReportsTab />}
          {activeTab === REGISTRY_SUBTABS.TREATMENT_NOTES && <RegistryTreatmentNotesTab />}
        </Box>
      </TopLevelPaper>
    </Box>
  );
});

export default Registry;
